/**
 * RCM Workspace — Thin orchestrator that imports tab components.
 * Refactored: each tab lives in /pages/rcm/{TabName}.js
 */
import React, { useState, useEffect, useCallback } from 'react';
import { api } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Send, ShieldAlert, FileText, ClipboardCheck, Clock,
  X, CheckCircle2, XCircle, Info, ArrowRight,
  ListChecks, Eye, Zap, Radio
} from 'lucide-react';

// Tab components
import { BriefingTab } from './rcm/BriefingTab';
import { SubmissionTab } from './rcm/SubmissionTab';
import { RejectionsTab } from './rcm/RejectionsTab';
import { DenialsTab } from './rcm/DenialsTab';
import { RemittanceTab } from './rcm/RemittanceTab';
import { PostingTab } from './rcm/PostingTab';
import { FollowUpTab } from './rcm/FollowUpTab';
import { AssignmentsTab } from './rcm/AssignmentsTab';
import { InsightsTab } from './rcm/InsightsTab';
import { IntelligenceTab } from './rcm/IntelligenceTab';
import { ExecutionQueueTab } from './rcm/ExecutionQueueTab';
import { OperationsTab } from './rcm/OperationsTab';

// Filter context for cross-tab navigation
const FilterContext = React.createContext({
  filters: {}, source: null, setFilters: () => {}, clearFilters: () => {},
});

function useFilters() { return React.useContext(FilterContext); }

function ActiveFilterBanner() {
  const { filters, source, clearFilters } = useFilters();
  const keys = Object.keys(filters || {}).filter(k => filters[k] != null && filters[k] !== '');
  if (keys.length === 0) return null;
  return (
    <div className="bg-sky-50 border border-sky-200 rounded px-3 py-1.5 flex items-center gap-2 text-xs" data-testid="active-filter-banner">
      <Info className="h-3 w-3 text-sky-600 shrink-0" />
      <span className="text-sky-800">
        Filtered{source ? ` from ${source}` : ''}: {keys.map(k => <Badge key={k} variant="outline" className="text-[9px] mx-0.5 py-0">{k}={String(filters[k])}</Badge>)}
      </span>
      <Button variant="ghost" size="sm" className="h-5 text-[10px] px-1.5 ml-auto" onClick={clearFilters} data-testid="clear-filters-btn">
        <X className="h-2.5 w-2.5 mr-0.5" />Clear
      </Button>
    </div>
  );
}

export default function RcmWorkspacePage() {
  const { user } = useAuth();
  const [tab, setTab] = useState('briefing');
  const [dataMode, setDataMode] = useState(null);
  const [followUpCount, setFollowUpCount] = useState(0);
  const [rejCount, setRejCount] = useState(0);
  const [denCount, setDenCount] = useState(0);

  const [filters, setFiltersRaw] = useState({});
  const [filterSource, setFilterSource] = useState(null);

  const setFilters = useCallback((newFilters, source = null) => {
    setFiltersRaw(newFilters || {});
    setFilterSource(source);
  }, []);

  const clearFilters = useCallback(() => {
    setFiltersRaw({});
    setFilterSource(null);
  }, []);

  const navigateWithFilter = useCallback((targetTab, filterObj, source = 'briefing card') => {
    setFilters(filterObj, source);
    setTab(targetTab);
    api.post('/rcm/briefing/log', {
      action_type: 'card_click', target_tab: targetTab, filters: filterObj, context: { source },
    }).catch(() => {});
  }, [setFilters]);

  const handleTabChange = useCallback((newTab) => {
    if (newTab !== tab) clearFilters();
    setTab(newTab);
  }, [tab, clearFilters]);

  useEffect(() => {
    api.get('/rcm-analytics/admin/data-mode').then(r => setDataMode(r.data)).catch(() => {});
    api.get('/rcm/follow-up/summary').then(r => setFollowUpCount(r.data.total_count || 0)).catch(() => {});
    api.get('/rcm/rejections/queue').then(r => setRejCount(r.data.count || 0)).catch(() => {});
    api.get('/rcm/denials/queue').then(r => setDenCount(r.data.count || 0)).catch(() => {});
  }, []);

  const role = (typeof window !== 'undefined' && localStorage.getItem('hfc_dev_role')) || user?.role || '';
  const isSu = user?.is_super_user && !localStorage.getItem('hfc_dev_role');
  const isBiller = ['billing', 'admin', 'office_manager'].includes(role) || isSu;

  return (
    <FilterContext.Provider value={{ filters, source: filterSource, setFilters, clearFilters }}>
    <div className="p-4 md:p-6 space-y-4 max-w-[1440px]" data-testid="rcm-workspace">
      {dataMode?.mode === 'demo' && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg px-3 py-1.5 flex items-center gap-2 text-xs" data-testid="demo-banner">
          <Info className="h-3.5 w-3.5 text-amber-600 shrink-0" />
          <span className="text-amber-800">Demo Data Active — Workflows use seeded data. <span className="font-medium">Reset available in admin settings.</span></span>
        </div>
      )}
      {dataMode?.mode === 'production' && (
        <div className="bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-1.5 flex items-center gap-2 text-xs" data-testid="prod-banner">
          <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600 shrink-0" />
          <span className="text-emerald-800">Production Mode</span>
        </div>
      )}

      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold tracking-tight" data-testid="workspace-title">RCM Workspace</h1>
          <p className="text-xs text-muted-foreground">Revenue cycle execution — submit, correct, review, follow up</p>
        </div>
      </div>

      <Tabs value={tab} onValueChange={handleTabChange} data-testid="workspace-tabs">
        <TabsList className="bg-muted/50">
          <TabsTrigger value="briefing" data-testid="tab-briefing"><Eye className="h-3 w-3 mr-1.5" />Briefing</TabsTrigger>
          {isBiller && <TabsTrigger value="submission" data-testid="tab-submission"><Send className="h-3 w-3 mr-1.5" />Submission</TabsTrigger>}
          {isBiller && <TabsTrigger value="rejections" data-testid="tab-rejections" className="relative">
            <XCircle className="h-3 w-3 mr-1.5" />Rejections
            {rejCount > 0 && <Badge variant="secondary" className="ml-1.5 text-[9px] h-4 px-1 min-w-[18px]">{rejCount}</Badge>}
          </TabsTrigger>}
          {isBiller && <TabsTrigger value="denials" data-testid="tab-denials" className="relative">
            <ShieldAlert className="h-3 w-3 mr-1.5" />Denials
            {denCount > 0 && <Badge variant="destructive" className="ml-1.5 text-[9px] h-4 px-1 min-w-[18px]">{denCount}</Badge>}
          </TabsTrigger>}
          {isBiller && <TabsTrigger value="remittance" data-testid="tab-remittance"><FileText className="h-3 w-3 mr-1.5" />Remittance</TabsTrigger>}
          {isBiller && <TabsTrigger value="posting" data-testid="tab-posting"><ClipboardCheck className="h-3 w-3 mr-1.5" />Posting Review</TabsTrigger>}
          {isBiller && <TabsTrigger value="followup" data-testid="tab-followup" className="relative">
            <Clock className="h-3 w-3 mr-1.5" />Follow-Up
            {followUpCount > 0 && <Badge variant="default" className="ml-1.5 text-[9px] h-4 px-1 min-w-[18px]">{followUpCount}</Badge>}
          </TabsTrigger>}
          {isBiller && <TabsTrigger value="assignments" data-testid="tab-assignments"><ListChecks className="h-3 w-3 mr-1.5" />Assignments</TabsTrigger>}
          {isBiller && <TabsTrigger value="insights" data-testid="tab-insights"><Zap className="h-3 w-3 mr-1.5" />Insights</TabsTrigger>}
          {isBiller && <TabsTrigger value="operations" data-testid="tab-operations"><Radio className="h-3 w-3 mr-1.5" />Operations</TabsTrigger>}
          {isBiller && <TabsTrigger value="execution" data-testid="tab-execution"><ArrowRight className="h-3 w-3 mr-1.5" />Execution</TabsTrigger>}
          {isBiller && <TabsTrigger value="intelligence" data-testid="tab-intelligence"><Zap className="h-3 w-3 mr-1.5" />Intelligence</TabsTrigger>}
        </TabsList>

        <TabsContent value="briefing"><BriefingTab onNavigate={navigateWithFilter} /></TabsContent>
        <TabsContent value="submission"><ActiveFilterBanner /><SubmissionTab /></TabsContent>
        <TabsContent value="rejections"><ActiveFilterBanner /><RejectionsTab /></TabsContent>
        <TabsContent value="denials"><ActiveFilterBanner /><DenialsTab /></TabsContent>
        <TabsContent value="remittance"><ActiveFilterBanner /><RemittanceTab /></TabsContent>
        <TabsContent value="posting"><ActiveFilterBanner /><PostingTab /></TabsContent>
        <TabsContent value="followup"><ActiveFilterBanner /><FollowUpTab /></TabsContent>
        <TabsContent value="assignments"><ActiveFilterBanner /><AssignmentsTab /></TabsContent>
        <TabsContent value="insights"><ActiveFilterBanner /><InsightsTab /></TabsContent>
        <TabsContent value="operations"><ActiveFilterBanner /><OperationsTab /></TabsContent>
        <TabsContent value="execution"><ActiveFilterBanner /><ExecutionQueueTab /></TabsContent>
        <TabsContent value="intelligence"><ActiveFilterBanner /><IntelligenceTab /></TabsContent>
      </Tabs>
    </div>
    </FilterContext.Provider>
  );
}
